import { FeatureSection } from "./FeatureSection";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Search, BookOpen, MessageSquare, FileCheck, Quote } from "lucide-react";
import { useTranslation } from "react-i18next";

export const RAGKnowledgeSection = () => {
  const { t } = useTranslation();
  const { ref: visualRef, isVisible: visualVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <FeatureSection id="rag-knowledge" background="muted">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Left: Visual (reversed order on desktop) */}
        <div
          ref={visualRef}
          className={cn(
            "relative order-2 lg:order-1 animate-scale-in",
            visualVisible && "is-visible"
          )}
        >
          {/* Chat Interface Mockup */}
          <div className="relative w-full max-w-lg mx-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50 bg-muted/30">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <MessageSquare className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-sm font-medium">{t("landing.ragKnowledge.demo.chatTitle")}</div>
                <div className="text-[10px] text-muted-foreground">{t("landing.ragKnowledge.demo.deepMode")}</div>
              </div>
            </div>

            {/* Chat Body */}
            <div className="p-4 space-y-4 min-h-[280px]">
              {/* User Message */}
              <div className="flex justify-end">
                <div className="bg-primary text-primary-foreground px-4 py-2.5 rounded-2xl rounded-tr-sm max-w-[85%] text-sm">
                  {t("landing.ragKnowledge.demo.userMessage")}
                </div>
              </div>

              {/* AI Response */}
              <div className="space-y-3">
                <div className="bg-muted rounded-2xl rounded-tl-sm p-4 max-w-[90%] text-sm space-y-3">
                  <p className="text-foreground">
                    {t("landing.ragKnowledge.demo.aiResponseIntro")}
                  </p>
                  <ul className="text-muted-foreground space-y-1.5 pl-4">
                    <li>• {t("landing.ragKnowledge.demo.decision1")}</li>
                    <li>• {t("landing.ragKnowledge.demo.decision2")}</li>
                    <li>• {t("landing.ragKnowledge.demo.decision3")}</li>
                  </ul>
                </div>

                {/* Source Cards */}
                <div className="flex gap-2 overflow-x-auto pb-1">
                  <SourceCard title={t("landing.ragKnowledge.demo.sourceQ1")} score={95} matchLabel={t("landing.ragKnowledge.demo.matchScore", { score: 95 })} />
                  <SourceCard title={t("landing.ragKnowledge.demo.sourceBudget")} score={87} matchLabel={t("landing.ragKnowledge.demo.matchScore", { score: 87 })} />
                </div>
              </div>
            </div>
          </div>

          {/* Floating elements */}
          <div className="absolute -left-4 top-24 bg-card p-3 rounded-xl shadow-xl border border-border animate-float hidden lg:block">
            <Search className="w-5 h-5 text-primary" />
          </div>
          <div className="absolute -right-4 bottom-24 bg-card p-3 rounded-xl shadow-xl border border-border animate-float hidden lg:block" style={{ animationDelay: "1.2s" }}>
            <Quote className="w-5 h-5 text-accent" />
          </div>
        </div>

        {/* Right: Copy */}
        <div className="space-y-6 text-center lg:text-left order-1 lg:order-2 max-w-xl mx-auto lg:mx-0 lg:max-w-none">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-600 dark:text-purple-400 text-xs font-medium">
            <BookOpen className="h-3.5 w-3.5" />
            {t("landing.ragKnowledge.badge")}
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.1]">
            {t("landing.ragKnowledge.title")}{" "}
            <span className="text-gradient">{t("landing.ragKnowledge.titleHighlight")}</span>
          </h2>

          <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
            {t("landing.ragKnowledge.description")}
          </p>

          {/* Features list */}
          <div className="grid sm:grid-cols-2 gap-4 pt-4 justify-items-center lg:justify-items-start">
            <FeatureItem icon={Search} text={t("landing.ragKnowledge.features.semanticSearch")} />
            <FeatureItem icon={Quote} text={t("landing.ragKnowledge.features.sourceCitations")} />
            <FeatureItem icon={FileCheck} text={t("landing.ragKnowledge.features.confidenceScores")} />
            <FeatureItem icon={MessageSquare} text={t("landing.ragKnowledge.features.conversationalAI")} />
          </div>
        </div>
      </div>
    </FeatureSection>
  );
};

const FeatureItem = ({ icon: Icon, text }: { icon: React.ElementType; text: string }) => (
  <div className="flex items-center gap-3 text-sm text-muted-foreground">
    <div className="p-2 rounded-lg bg-accent/10">
      <Icon className="h-4 w-4 text-accent" />
    </div>
    <span>{text}</span>
  </div>
);

const SourceCard = ({ title, matchLabel }: { title: string; score: number; matchLabel: string }) => (
  <div className="shrink-0 px-3 py-2 rounded-lg bg-background border border-border text-xs">
    <div className="font-medium text-foreground truncate max-w-[120px]">{title}</div>
    <div className="text-muted-foreground mt-0.5">{matchLabel}</div>
  </div>
);
